import {createContext,useCallback,useContext,useEffect,useId,useMemo,useRef,useState,type FormEvent,type FormHTMLAttributes,type ReactNode} from 'react';
import {useBlocker} from 'react-router';
import {ConfirmationDialog} from './ConfirmationDialog';
import {ErrorSummary,type ErrorSummaryItem} from './ErrorSummary';
import {showFormErrors} from './form-errors';
import {UNSAVED_CHANGES,dedupeIssues,messageForValidity,openCollapsedAncestors,type ParentLike} from './form-validation';

/**
 * The shared form wrapper (audit items #2, #9, #11, #62). It gives every form:
 * - `noValidate`, so validation is ours and identical in every browser;
 * - on submit, an error summary that takes focus and links to each field, plus a
 *   message beside each field (`aria-invalid`, `aria-describedby`);
 * - collapsed <details> sections that contain an invalid field are opened first,
 *   so the field can be focused;
 * - an unsaved-changes guard for in-app navigation and page unload, using one
 *   dialog: "Discard unsaved changes?" [Keep editing] [Discard].
 * Server `fieldErrors` (see api-error.ts) go in `serverErrors` and are shown the same way.
 * `onSubmit` may return `true` (or a promise of `true`) once the save is confirmed,
 * which clears the unsaved-changes state.
 */
export type FormProps=Omit<FormHTMLAttributes<HTMLFormElement>,'onSubmit'|'noValidate'>&{
  onSubmit?:(event:FormEvent<HTMLFormElement>)=>void|boolean|Promise<void|boolean>;
  /** Controlled dirty state. Leave undefined to track edits automatically. */
  dirty?:boolean;
  onDirtyChange?:(dirty:boolean)=>void;
  serverErrors?:Readonly<Record<string,string>>;
  errorTitle?:string;
  /** Set false for forms that never lose work (for example a filter form). */
  guardUnsavedChanges?:boolean;
  children:ReactNode;
};

type FormStatus={dirty:boolean;markClean:()=>void;markDirty:()=>void};
const FormStatusContext=createContext<FormStatus|null>(null);

/** Lets a save handler inside a Form clear or set the unsaved-changes state. */
export function useFormStatus():FormStatus{
  return useContext(FormStatusContext)??{dirty:false,markClean:()=>undefined,markDirty:()=>undefined};
}

type Control=HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement;
const isControl=(element:Element|RadioNodeList|null|undefined):element is Control=>
  element instanceof HTMLInputElement||element instanceof HTMLSelectElement||element instanceof HTMLTextAreaElement;

function labelText(control:Control):string{
  const label=control.labels?.[0];
  if(label){
    const copy=label.cloneNode(true) as HTMLElement;
    copy.querySelectorAll('input,select,textarea,small,.field-message,.field-error,.sr-only').forEach(node=>node.remove());
    const text=copy.textContent?.replace(/\s+/g,' ').replace(/\s*\*$/,'').trim();
    if(text)return text;
  }
  const legend=control.closest('fieldset')?.querySelector('legend')?.textContent?.trim();
  return control.getAttribute('aria-label')?.trim()||legend||control.name||'This field';
}

function focusFirst(control:Control){
  control.focus({preventScroll:true});
  control.scrollIntoView({block:'center',inline:'nearest'});
}

function UnsavedChangesGuard({isDirty}:{isDirty:()=>boolean}){
  const blocker=useBlocker(()=>isDirty());
  useEffect(()=>{
    const beforeUnload=(event:BeforeUnloadEvent)=>{if(isDirty()){event.preventDefault();event.returnValue=''}};
    window.addEventListener('beforeunload',beforeUnload);
    return()=>window.removeEventListener('beforeunload',beforeUnload);
  },[isDirty]);
  return <ConfirmationDialog
    open={blocker.state==='blocked'}
    tone="warning"
    {...UNSAVED_CHANGES}
    onConfirm={()=>blocker.proceed?.()}
    onCancel={()=>blocker.reset?.()}
  />;
}

export function Form({onSubmit,dirty:controlledDirty,onDirtyChange,serverErrors,errorTitle='Check the form',guardUnsavedChanges=true,children,onInput,onReset,...formProps}:FormProps){
  const formRef=useRef<HTMLFormElement>(null);
  const idPrefix=useId();
  const [internalDirty,setInternalDirty]=useState(false);
  const [issues,setIssues]=useState<readonly ErrorSummaryItem[]>([]);
  const [source,setSource]=useState<'client'|'server'>('client');
  const [focusKey,setFocusKey]=useState(0);
  const dirty=controlledDirty??internalDirty;
  const dirtyRef=useRef(dirty);
  dirtyRef.current=dirty;

  const setDirty=useCallback((value:boolean)=>{
    dirtyRef.current=value;
    setInternalDirty(value);
    onDirtyChange?.(value);
  },[onDirtyChange]);
  const status=useMemo<FormStatus>(()=>({dirty,markClean:()=>setDirty(false),markDirty:()=>setDirty(true)}),[dirty,setDirty]);
  const isDirty=useCallback(()=>dirtyRef.current,[]);

  const report=useCallback((form:HTMLFormElement,entries:ReadonlyArray<{control:Control;message:string}>,origin:'client'|'server')=>{
    const items:ErrorSummaryItem[]=[];
    const byName:Record<string,string>={};
    entries.forEach(({control,message},index)=>{
      if(!control.id)control.id=`${idPrefix}-field-${index}`;
      openCollapsedAncestors(control as unknown as {parentElement:ParentLike|null});
      items.push({targetId:control.id,label:labelText(control),message});
      if(control.name)byName[control.name]=message;
    });
    showFormErrors(form,byName);
    setSource(origin);
    setIssues(dedupeIssues(items));
    setFocusKey(key=>key+1);
    // showFormErrors focuses the first field; the summary then takes focus and links to every field.
  },[idPrefix]);

  useEffect(()=>{
    const form=formRef.current;
    if(!form||!serverErrors)return;
    const entries:Array<{control:Control;message:string}>=[];
    for(const [name,message] of Object.entries(serverErrors)){
      const field=form.elements.namedItem(name);
      const control:Element|null|undefined=field instanceof RadioNodeList?(Array.from(field).find(item=>isControl(item as Element)) as Element|undefined):field;
      if(control&&isControl(control))entries.push({control,message});
    }
    if(entries.length)report(form,entries,'server');
  },[serverErrors,report]);

  async function submit(event:FormEvent<HTMLFormElement>){
    const form=event.currentTarget;
    const submitter=(event.nativeEvent as SubmitEvent).submitter;
    const skipValidation=submitter?.hasAttribute('formnovalidate')===true;
    if(!skipValidation&&!form.checkValidity()){
      event.preventDefault();
      const entries=Array.from(form.elements)
        .filter((element):element is Control=>isControl(element)&&element.willValidate&&!element.validity.valid)
        .map(control=>({control,message:messageForValidity(control.validity,labelText(control),control)}));
      report(form,entries,'client');
      return;
    }
    if(issues.length){showFormErrors(form,{});setIssues([])}
    const result=await onSubmit?.(event);
    if(result===true)setDirty(false);
  }

  return <FormStatusContext.Provider value={status}>
    <form
      {...formProps}
      ref={formRef}
      noValidate
      onSubmit={event=>void submit(event)}
      onInput={event=>{
        onInput?.(event);
        const target=event.target as HTMLElement;
        if(target.closest?.('[data-form-ignore]'))return;
        if(!dirtyRef.current)setDirty(true);
      }}
      onReset={event=>{onReset?.(event);setDirty(false);setIssues([])}}
    >
      <ErrorSummary title={errorTitle} items={issues} source={source} focusKey={focusKey}/>
      {children}
      {guardUnsavedChanges&&dirty&&<UnsavedChangesGuard isDirty={isDirty}/>}
    </form>
  </FormStatusContext.Provider>;
}

/** The action row of a Form: a sticky bar that says when there are unsaved changes. */
export function FormActions({children,className}:{children:ReactNode;className?:string}){
  const {dirty}=useFormStatus();
  return <div className={['form-action-bar',className].filter(Boolean).join(' ')} data-dirty={dirty||undefined}>
    <p className="form-action-bar__status" role="status">{dirty?'You have unsaved changes.':''}</p>
    <div className="form-action-bar__actions">{children}</div>
  </div>;
}
